import React, { useCallback, useEffect, useRef, useState } from "react";
import { 
  Route, 
  Routes 
} from "react-router-dom";

import { CORE_DATA_TYPES, DATA_TYPE_INFO } from "@dndModels/Constants";
import { DataType } from "@dndModels/Types";
import { useAuth } from "react-oidc-context";

import UserIndex from "@views/user";
import UserEdit from "@views/user/edit";

import Attributions from "@views/attributions";
import OGL from "@views/ogl";
import PrivacyPolicy from "@views/PrivacyPolicy";
import DataDeletion from "@views/DataDeletion";

import Login from "@components/Login";
import DandDAPI from "@utilities/smart_api";
import DandDCharacterUtilities from '@dndUtilities/char_utilities';
// import UserAPI from "../utilities/user_api";

import LoadingBox from "@components/input/LoadingBox";

import AdminOnly from "@dndViews/admin/AdminOnly";
import FeatureUsage from "@dndViews/admin/FeatureUsage";
import SpellListRepair from "@dndViews/admin/SpellListRepair";
import TableFieldRepair from "@dndViews/admin/TableFieldRepair";

import HomePage from "@views/Home";

import SpellParseAdd from "@dndViews/spell/parseAdd";

import CampaignIndex from "@dndViews/campaign";
import CampaignDetails from "@dndViews/campaign/details";
import CampaignJoin from "@dndViews/campaign/join";
import CampaignDecks from "@dndViews/campaign/decks";

import CharacterIndex from "@dndViews/character";
import CharacterDetails from "@dndViews/character/details";
import CharacterShared from "@dndViews/character/shared";
import AllCharacters from "@dndViews/character/all";
import CharacterPrintable from "@dndViews/character/printable";

import CreatureMergeAdd from "@dndViews/creature/mergeAdd";
import CreatureParseAdd from "@dndViews/creature/parseAdd";

import ItemParseAdd from "@dndViews/item/parseAdd";
import GiftParseAdd from "@dndViews/gift/parseAdd";

import NewUserInput from "@components/input/NewUserInput";

import StandardIndex from "./StandardIndex";
import StandardEdit from "./StandardEdit";
import ChatsButton from "@dndComponents/chat/ChatsButton";
import ApiLoadStatusOverlay from "@components/display/ApiLoadStatusOverlay";

const COGNITO_DOMAIN = "https://us-west-2h7ntbqeqs.auth.us-west-2.amazoncognito.com";
const CLIENT_ID = "1ltrrg50umnijv7h38aku4ufpr";
const isLocal = window.location.hostname === "localhost";
const LOGOUT_URI = isLocal
  ? "http://localhost:3000"
  : "https://hbgamelabs.com";
  
interface Props { 
  loginOpen: boolean;
  userStatus: "" | "New User" | "Updating User" | "Error";
  onNewUserComplete: () => void;
}

const data_type_map: { [data_type: string]: string } = {}; 
CORE_DATA_TYPES.forEach(o => {
  const dt_info = DATA_TYPE_INFO.find(o2 => o2.data_type === o);
  if (dt_info) {
    data_type_map[o] = dt_info.plural;
  }
}); 

const api = DandDAPI.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

export default function DandDMainPage({ 
  loginOpen,
  userStatus,
  onNewUserComplete
}: Props) {
  // console.log("DandDMainPage");
  const auth = useAuth();
  // console.log("after auth");
  const [fetched, setFetched] = useState(false);
  const mounted = useRef(false);
  const has_loaded_once = useRef(false);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const authenticated = useCallback(() => {
    api.loadMyThings();
  }, []);

  const load = useCallback(() => {
    console.log("load()");
    if (!fetched) {
      setFetched(true);
    }
  }, [fetched]);

  const reload = useCallback(() => {
    console.log("reload");
    // The very first "Reload" (normal app boot) shouldn't wipe anything - the light
    // data App.tsx started fetching before auth even resolved is already loading/
    // loaded, and we want to keep it. Only genuine re-loads (spoof/unspoof, logout
    // then log back in, a different user in the same tab) should clear and refetch.
    if (has_loaded_once.current) {
      api.smart_hash = {};
      api.temp_hash = {};
      api.live_smart_hash = {};
      api.load_promises = {};
      // Otherwise types stay "done" with empty hashes, and nothing reloads them.
      api.load_status.clear();
      clearInterval(api.temp_hash_interval);
      api.temp_hash_interval = null;
      // data_util's per-character cache was filtered for the previous user.
      char_util.emptyHash();
      // Start over from the public source books, then extend to whoever the user is now.
      api.resetSourceBookScope();
      api.loadDataType("source_book", "priority", { onComplete: () => {
        api.queueStandardLoad({ data_types: CORE_DATA_TYPES as DataType[] });
      }});
      if (api.user) {
        api.loadMyThings();
      }
    }
    has_loaded_once.current = true;
    load();
  }, [load]);

  // ── Sign Out ──────────────────────────────────────────────────────────────
  const handleSignOut = useCallback(() => {
    if (auth.isAuthenticated) {
      localStorage.setItem("redirectRememberURL", window.location.href);
      auth.removeUser();
      window.location.href = `${COGNITO_DOMAIN}/logout?client_id=${CLIENT_ID}&logout_uri=${encodeURIComponent(LOGOUT_URI)}`;
    } else {
      api.logout();
    }
  }, [auth]);

  useEffect(() => {
    api.subscription_holder.subscribe("Authenticated", mounted, authenticated);
    api.subscription_holder.subscribe("Reload", mounted, reload);
    api.subscription_holder.subscribe("Logout", mounted, handleSignOut);
  }, [reload, handleSignOut]);

  if (userStatus !== "") {
    return (
      <NewUserInput 
        userStatus={userStatus} 
        onComplete={onNewUserComplete} 
      />
    );
  // } else if (!fetched) {
  //   return (<LoadingBox message="Loading" size={window.innerHeight * 0.5} height={window.innerHeight - 64} width={window.innerWidth - 24} />); // width={window.innerWidth - 17}
  } else if (loginOpen) {
    return (
      <Login />
    );
  } else {
    console.log("Main Page");
    return (
      <>
        <Routes>
          <Route path="/" element={<HomePage/>} />
          <Route path="" element={<HomePage/>} />

          {/* Special Pages that don't fit the normal formula */}
          <Route path="/home" element={<HomePage/>} />
          <Route path="/attributions" element={<Attributions/>} />
          <Route path="/ogl" element={<OGL/>} />
          <Route path="/privacy" element={<PrivacyPolicy/>} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          <Route path="/admin_only" element={<AdminOnly/>} />
          <Route path="/admin/feature_usage" element={<FeatureUsage/>} />
          <Route path="/admin/spell_list_repair" element={<SpellListRepair/>} />
          <Route path="/admin/table_field_repair" element={<TableFieldRepair/>} />
          <Route path="/spell/parse_add" element={<SpellParseAdd/>} />
          <Route path="/item/parse_add" element={<ItemParseAdd/>} />
          <Route path="/gift/parse_add" element={<GiftParseAdd/>} />
          {/* <Route path="/creature/from_character/:char_id" element={<CreatureEdit/>} /> */}
          <Route path="/creature/merge_add" element={<CreatureMergeAdd/>} />
          <Route path="/creature/parse_add" element={<CreatureParseAdd/>} />
          <Route path="/all_characters" element={<AllCharacters/>} />
          {/* <Route path="/source_book/create" element={<SourceBookEdit/>} />
          <Route path="/source_book/:id_or_abbrev" element={<SourceBookEdit/>} />
          <Route path="/source_book" element={<SourceBookIndex/>} /> */}
          
          {/* User Focused Pages */}
          <Route path="/campaign/decks/:id" element={<CampaignDecks/>} />
          <Route path="/campaign/join/:id/:invite" element={<CampaignJoin/>} />
          <Route path="/campaign/create" element={<CampaignDetails/>} />
          <Route path="/campaign/:id" element={<CampaignDetails/>} />
          <Route path="/campaign" element={<CampaignIndex/>} />

          <Route path="/character/create_on_campaign/:campaign_id/:unassigned" element={<CharacterDetails/>} />
          <Route path="/character/shared/:id" element={<CharacterShared/>} />
          <Route path="/character/printable/:id" element={<CharacterPrintable/>} />
          <Route path="/character/:id" element={<CharacterDetails/>} />
          <Route path="/character" element={<CharacterIndex/>} />

          <Route path="/user" element={<UserIndex/>} />
          <Route path="/user/:id" element={<UserEdit/>} />

          <Route path="/:data_type/:sb_abbrev/:name" element={<StandardEdit />} />
          <Route path="/:data_type/:id_or_name" element={<StandardEdit />} />
          <Route path="/:data_type" element={<StandardIndex />} />
        </Routes>
        <ChatsButton />
        <ApiLoadStatusOverlay />
      </>
    );
  }
}
